import {
    CropTypeCodeEnum,
    CropTypeCodeEnumSchema,
} from "@campaign/domain/models/crop-type-code.enum";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const CropTypeSchema = z
    .object({
        id: zIdSchema,
        code: CropTypeCodeEnumSchema,
        agents: z.boolean(),
        createAt: z.string().trim().optional(),
        updateAt: z.string().trim().optional(),
    })
    .brand<"CropType">();

export type CropTypeInputProps = z.input<typeof CropTypeSchema>;
/** `createWithRecovery` takes the raw backend `code`: the schema validates it and recovers an unknown one. */
type CropTypeRecoveryInputProps = Omit<CropTypeInputProps, "code"> & {
    code: string;
};
export type CropTypeProps = z.output<typeof CropTypeSchema>;

export class CropType {
    private constructor(private readonly props: Readonly<CropTypeProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get code(): CropTypeCodeEnum {
        return this.props.code;
    }

    get agents(): boolean {
        return this.props.agents;
    }

    get createAt(): Undefinable<string> {
        return this.props.createAt;
    }

    get updateAt(): Undefinable<string> {
        return this.props.updateAt;
    }

    static validate(
        props: Readonly<MaybeValidProps<CropTypeInputProps>>,
    ): Result<CropTypeProps, z.ZodError<CropTypeProps>> {
        const result = CropTypeSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<CropTypeRecoveryInputProps>>,
    ): Result<CropTypeProps, PartialValid<typeof CropType>> {
        const result = partialSafeParse(CropTypeSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<CropTypeInputProps>,
    ): Result<CropType, z.ZodError<CropTypeProps>> {
        return CropType.validate(props).map(
            (validatedProps) => new CropType(validatedProps),
        );
    }

    static createWithRecovery(
        props: CropTypeRecoveryInputProps,
    ): Result<CropType, ModelError<typeof CropType>> {
        return CropType.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<CropTypeProps> = {
                    id: error.validProps.id,
                    code: CropTypeCodeEnum.BROCCOLI,
                    agents: false,
                    createAt: "",
                    updateAt: "",
                };

                const recoveryProps: Unbrand<CropTypeProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof CropType>(
                    new CropType(recoveryProps as CropTypeProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new CropType(validatedProps));
    }

    toCreateProps(): CropTypeInputProps {
        return {
            id: this.props.id.toCreateProps(),
            code: this.props.code,
            agents: this.props.agents,
            createAt: this.props.createAt,
            updateAt: this.props.updateAt,
        };
    }
}

const CropTypesSchema = z
    .object({
        cropTypes: z.array(
            z.custom<CropType>((val) => val instanceof CropType, {
                error: "Invalid CropType instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"CropTypes">();

export type CropTypesInputProps = z.input<typeof CropTypesSchema>;
export type CropTypesProps = z.output<typeof CropTypesSchema>;

export class CropTypes {
    private constructor(private readonly props: Readonly<CropTypesProps>) {}

    get cropTypes(): readonly Readonly<CropType>[] {
        return this.props.cropTypes;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): CropTypes {
        return CropTypes.create({
            cropTypes: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<CropTypesInputProps>>,
    ): Result<CropTypesProps, z.ZodError<CropTypesProps>> {
        const result = CropTypesSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<CropTypesInputProps>,
    ): Result<CropTypes, z.ZodError<CropTypesProps>> {
        return CropTypes.validate(props).map(
            (validatedProps) => new CropTypes(validatedProps),
        );
    }
}
